import cn from 'classnames';
import { Button } from '@Components/Button';
import { Size } from '@Components/designConstants';
import { HandoffScenario, NEWTON_HANDOFF } from './elevate-newton-data';

// Step 1 of the two-stage demo. The team has just run the full Newton framework
// LIVE in Newton; this step RECEIVES that handoff — the selected hypothesis, the
// audience, the Get/To/By comms strategy, the chosen investment scenario, and the
// streaming/video budget split (CTV/direct-IO + OLV/Nexxen programmatic) — and
// hands it to the streaming planner. It is a recap, not a re-run: Newton already
// happened on the team's screen.
interface NewtonHandoffStepProps {
  blurb: string;
  approvedBudget: number | undefined;
  onApprove: (streamingBudget: number) => void;
}

const usd = (value: number): string => `$${value.toLocaleString('en-US')}`;
const millions = (value: number): string => `${(value / 1_000_000).toFixed(1)}M`;

export const NewtonHandoffStep = ({ blurb, approvedBudget, onApprove }: NewtonHandoffStepProps) => (
  <div className='elevate-handoff'>
    <p className='elevate-demo-blurb'>{blurb}</p>

    <section className='elevate-handoff-card'>
      <h3 className='elevate-handoff-card-title'>
        Selected hypothesis · {NEWTON_HANDOFF.hypothesisLabel}
      </h3>
      <p className='elevate-handoff-text'>{NEWTON_HANDOFF.selectedHypothesis}</p>
    </section>

    <section className='elevate-handoff-card'>
      <h3 className='elevate-handoff-card-title'>Markets &amp; flight</h3>
      <p className='elevate-handoff-text'>
        Launch markets: <strong>{NEWTON_HANDOFF.markets.join(', ')}</strong> — modeled in detail for{' '}
        <strong>{NEWTON_HANDOFF.detailMarket}</strong>. Flight:{' '}
        <strong>{NEWTON_HANDOFF.flight}</strong>.
      </p>
    </section>

    <section className='elevate-handoff-card'>
      <h3 className='elevate-handoff-card-title'>Audience</h3>
      <div className='elevate-handoff-audience'>
        <span className='elevate-handoff-audience-size'>
          {millions(NEWTON_HANDOFF.primaryAudience.size)}
        </span>
        <div>
          <div className='elevate-handoff-audience-name'>{NEWTON_HANDOFF.primaryAudience.name}</div>
          <p className='elevate-handoff-text'>{NEWTON_HANDOFF.primaryAudience.note}</p>
        </div>
      </div>
      <p className='elevate-handoff-text'>
        <strong>Priority launch cohort:</strong> {NEWTON_HANDOFF.priorityCohort.name} (~
        {millions(NEWTON_HANDOFF.priorityCohort.size)}). Validates and refines the brief audience (
        {NEWTON_HANDOFF.briefAudience.name}) rather than replacing it.
      </p>
    </section>

    <section className='elevate-handoff-card'>
      <h3 className='elevate-handoff-card-title'>Communications strategy · Get / To / By</h3>
      <dl className='elevate-handoff-gtb'>
        <div>
          <dt>Get</dt>
          <dd>{NEWTON_HANDOFF.commsStrategy.get}</dd>
        </div>
        <div>
          <dt>To</dt>
          <dd>{NEWTON_HANDOFF.commsStrategy.to}</dd>
        </div>
        <div>
          <dt>By</dt>
          <dd>{NEWTON_HANDOFF.commsStrategy.by}</dd>
        </div>
      </dl>
    </section>

    <section className='elevate-handoff-card'>
      <h3 className='elevate-handoff-card-title'>Investment scenario</h3>
      <ul className='elevate-handoff-scenarios'>
        {NEWTON_HANDOFF.scenarios.map(scenario => (
          <ScenarioRow key={scenario.key} scenario={scenario} />
        ))}
      </ul>
    </section>

    <section className='elevate-handoff-card'>
      <h3 className='elevate-handoff-card-title'>Planning assumptions</h3>
      <ul className='elevate-handoff-scenarios'>
        {NEWTON_HANDOFF.planningAssumptions.map(assumption => (
          <li key={assumption} className='elevate-handoff-text'>
            {assumption}
          </li>
        ))}
      </ul>
    </section>

    <section className='elevate-handoff-card'>
      <h3 className='elevate-handoff-card-title'>Flighting · build → burst → sustain</h3>
      <ul className='elevate-handoff-scenarios'>
        {NEWTON_HANDOFF.flighting.map(phase => (
          <li key={phase.label} className='elevate-handoff-scenario'>
            <div className='elevate-handoff-scenario-head'>
              <span className='elevate-handoff-scenario-label'>
                {phase.label} · {phase.weeks} ({phase.dates})
              </span>
            </div>
            <p className='elevate-handoff-text'>
              CTV {usd(phase.ctvSpend)} · OLV {usd(phase.olvSpend)} — {phase.note}
            </p>
          </li>
        ))}
      </ul>
    </section>

    <ApproveControl approvedBudget={approvedBudget} onApprove={onApprove} />
  </div>
);

interface ScenarioRowProps {
  scenario: HandoffScenario;
}

const ScenarioRow = ({ scenario }: ScenarioRowProps) => (
  <li className={cn('elevate-handoff-scenario', { 'is-chosen': scenario.chosen })}>
    <div className='elevate-handoff-scenario-head'>
      <span className='elevate-handoff-scenario-label'>
        {scenario.key} · {scenario.label}
      </span>
      {scenario.chosen ? (
        <span className='elevate-handoff-chosen-tag'>recommended</span>
      ) : undefined}
    </div>
    <p className='elevate-handoff-text'>{scenario.emphasis}</p>
  </li>
);

interface ApproveControlProps {
  approvedBudget: number | undefined;
  onApprove: (streamingBudget: number) => void;
}

const ApproveControl = ({ approvedBudget, onApprove }: ApproveControlProps) => {
  const { ctvBudget, olvBudget, worldCupHoldout } = NEWTON_HANDOFF;
  const totalBudget = ctvBudget + olvBudget;

  if (approvedBudget !== undefined) {
    return (
      <div className='elevate-handoff-approved'>
        ✓ Handoff accepted — CTV {usd(ctvBudget)} + OLV {usd(olvBudget)} (CTV includes a reserved{' '}
        {usd(worldCupHoldout)} Netflix Women&apos;s World Cup sponsorship, held out of the planner).
      </div>
    );
  }

  return (
    <div className='elevate-handoff-accept'>
      <div className='elevate-handoff-budget'>
        Streaming budget from the {NEWTON_HANDOFF.scenarios.find(s => s.chosen)?.label} scenario:{' '}
        <strong>{usd(totalBudget)}</strong> — CTV {usd(ctvBudget)} (direct-IO + programmatic) + OLV{' '}
        {usd(olvBudget)} (Nexxen programmatic). The CTV total includes a reserved{' '}
        {usd(worldCupHoldout)} Netflix Women&apos;s World Cup streaming sponsorship, handled
        separately.
      </div>
      <Button size={Size.LG} onClick={() => onApprove(totalBudget)}>
        Accept handoff &amp; continue to planning
      </Button>
    </div>
  );
};
